/**
 * 来場者お知らせサービス
 * 公開中お知らせの取得を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';

/** お知らせ取得失敗エラーコード */
export const VISITOR_NOTICE_FETCH_FAILED_CODE = 'VISITOR_NOTICE_FETCH_FAILED';

/**
 * 公開中のお知らせを取得する
 * 重要お知らせを優先して先頭に並べ、次に公開日時の降順で返す
 * @param {number} [limit=5] - 取得上限
 * @returns {Promise<Array<{
 * noticeId: string,
 * title: string,
 * body: string,
 * targetRole: string,
 * isImportant: boolean,
 * publishedAt: string | null,
 * closedAt: string | null,
 * }>>} お知らせ一覧
 * @throws {AppError} 取得に失敗した場合
 */
export const visitorGetPublishedNotices = async (limit = 5) => {
  /** 正規化済み上限 */
  const normalizedLimit = Math.min(Math.max(Number(limit) || 5, 1), 20);

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** お知らせ取得結果（重要お知らせを先頭に、次に公開日時降順） */
  const { data, error } = await supabaseClient
    .from('notices')
    .select('id, title, body, target_role, is_important, published_at, closed_at')
    .eq('status', 'published')
    .in('target_role', ['all', 'visitor'])
    .order('is_important', { ascending: false })
    .order('published_at', { ascending: false })
    .limit(normalizedLimit);

  if (error) {
    logError('notices 取得に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      normalizedLimit,
    });

    throw new AppError('お知らせの取得に失敗しました。', {
      code: VISITOR_NOTICE_FETCH_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error.message,
      },
    });
  }

  if (!Array.isArray(data)) {
    throw new AppError('notices のレスポンス形式が不正です。', {
      code: VISITOR_NOTICE_FETCH_FAILED_CODE,
      statusCode: 500,
      details: { data },
    });
  }

  return data.map((noticeRow) => {
    /** お知らせID */
    const noticeId = noticeRow?.id;

    /** タイトル */
    const title = noticeRow?.title;

    /** 本文 */
    const body = noticeRow?.body;

    /** 対象ロール */
    const targetRole = noticeRow?.target_role;

    /** 公開日時 */
    const publishedAt = noticeRow?.published_at ?? null;

    /** 終了日時 */
    const closedAt = noticeRow?.closed_at ?? null;

    /** 重要フラグ */
    const isImportant = Boolean(noticeRow?.is_important ?? false);

    if (!noticeId || !title || !body || !targetRole) {
      throw new AppError('notices のレスポンス形式が不正です。', {
        code: VISITOR_NOTICE_FETCH_FAILED_CODE,
        statusCode: 500,
        details: { noticeRow },
      });
    }

    return {
      noticeId,
      title,
      body,
      targetRole,
      isImportant,
      publishedAt,
      closedAt,
    };
  });
};

